import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api, { API_BASE } from "../lib/api";
import { GEN_LABELS } from "../i18n";
import { FileDown, RefreshCw, Loader2, Check, TrendingDown, Briefcase, Zap, Heart, Star } from "lucide-react";

const DIM_LABELS = {
  EI: { E: "Extraversion", I: "Introversion" },
  SN: { S: "Sensing", N: "Intuition" },
  TF: { T: "Thinking", F: "Feeling" },
  JP: { J: "Judging", P: "Perceiving" },
};
const DIM_ORDER = { EI: ["E", "I"], SN: ["S", "N"], TF: ["T", "F"], JP: ["J", "P"] };

const DimBar = ({ dim, scores }) => {
  const [a, b] = DIM_ORDER[dim];
  const va = scores[a] || 0;
  const vb = scores[b] || 0;
  const dominant = va >= vb ? a : b;
  return (
    <div data-testid={`dim-${dim}`}>
      <div className="flex justify-between text-sm mb-2">
        <span style={{ color: dominant === a ? "var(--gold)" : "var(--text-dim)", fontWeight: dominant === a ? 600 : 400 }}>
          {DIM_LABELS[dim][a]} {va}%
        </span>
        <span style={{ color: dominant === b ? "var(--gold)" : "var(--text-dim)", fontWeight: dominant === b ? 600 : 400 }}>
          {vb}% {DIM_LABELS[dim][b]}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden flex" style={{ background: "var(--border)" }}>
        <div style={{ width: `${va}%`, background: "var(--gold)" }} />
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="card-amt p-6 animate-fade-up" style={{ opacity: 0 }}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="gold-text" />
      <h3 className="font-heading text-lg" style={{ color: "var(--text)" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {(items || []).map((it, i) => (
      <li key={i} className="flex gap-2 text-sm" style={{ color: "var(--text-dim)" }}>
        <Check size={16} className="gold-text mt-0.5 shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function ResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useAuth();
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/results/${resultId}`).then(({ data }) => setResult(data)).catch(() => setNotFound(true));
  }, [resultId]);

  if (notFound) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" style={{ color: "var(--text-dim)" }}>
          Result not found.
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin gold-text" size={32} />
      </div>
    );
  }

  const n = result.narrative || {};
  const tname = result.type_name?.[lang] || "";

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        {/* hero banner */}
        <div className="card-amt overflow-hidden mb-8 animate-fade-in" style={{ opacity: 0 }}>
          <div className="relative p-8 sm:p-12" style={{ background: "linear-gradient(135deg, #152A45, #0E1F33)" }}>
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--gold)" }}>{t("your_type")}</p>
            <div className="flex flex-wrap items-end gap-4">
              <h1 data-testid="result-type" className="font-heading text-6xl sm:text-7xl tracking-tight" style={{ color: "var(--gold)" }}>
                {result.mbti_type}
              </h1>
              <div className="pb-2">
                <div className="font-heading text-2xl" style={{ color: "var(--text)" }}>{tname}</div>
                <div className="text-sm" style={{ color: "var(--text-dim)" }}>
                  {result.name} · {GEN_LABELS[result.generation]}
                </div>
              </div>
            </div>
            {n.headline && (
              <p className="mt-5 font-body italic text-lg max-w-2xl" style={{ color: "var(--text)" }} data-testid="result-headline">
                "{n.headline}"
              </p>
            )}
          </div>
        </div>

        {/* actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <a
            data-testid="download-pdf-btn"
            href={`${API_BASE}/results/${resultId}/pdf`}
            className="btn-gold inline-flex items-center gap-2 px-5 py-3"
          >
            <FileDown size={18} /> {t("download_pdf")}
          </a>
          <button
            data-testid="retake-btn"
            onClick={() => navigate("/test")}
            className="btn-outline-gold inline-flex items-center gap-2 px-5 py-3"
          >
            <RefreshCw size={16} /> {t("retake")}
          </button>
        </div>

        {n.summary && (
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--text-dim)" }} data-testid="result-summary">
            {n.summary}
          </p>
        )}

        {/* dimensions */}
        <div className="card-amt p-6 sm:p-8 mb-6">
          <h3 className="font-heading text-lg mb-6" style={{ color: "var(--text)" }}>{t("dimensions")}</h3>
          <div className="space-y-6">
            {Object.keys(DIM_LABELS).map((dim) => (
              <DimBar key={dim} dim={dim} scores={result.dimension_scores?.[dim] || {}} />
            ))}
          </div>
        </div>

        {/* narrative grid */}
        <div className="grid md:grid-cols-2 gap-6">
          <Section icon={Zap} title={t("strengths")}><BulletList items={n.strengths} /></Section>
          <Section icon={TrendingDown} title={t("weaknesses")}><BulletList items={n.weaknesses} /></Section>
          <Section icon={Briefcase} title={t("careers")}><BulletList items={n.suitable_careers} /></Section>
          <Section icon={Heart} title={t("partners")}><BulletList items={n.compatible_partners} /></Section>
          <Section icon={Star} title={t("famous")}>
            <div className="flex flex-wrap gap-2">
              {(n.famous_people || []).map((p, i) => (
                <span key={i} className="px-3 py-1.5 rounded-sm text-sm" style={{ background: "rgba(201,165,87,0.12)", color: "var(--gold)" }}>
                  {p}
                </span>
              ))}
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
